/* Copyright 2019 VMware, Inc. All rights reserved. -- VMware Confidential */
module platform {

export interface ModalInfo {
   title: string;
   subtitle: string;
   width: number|string;
   height: number|string;
}

type ModalHierarchyContext = ModalInfo[];

export class ModalHelperService {

   /**
    * @param bottomToTop
    *   Whether to return the context info from the  bottom most to the top-most or
    *   reversed. The latter also makes sense - as visualization rotation (needed
    *   due to vertical text) will then reverse the order of the elements representing
    *   the hierarchy.
    */
   public discoverHierarchyContext(
         bottomToTop: boolean = true): ModalHierarchyContext {
      // NOTE: We need to collect all popups with a single
      // css selector to have them properly ordered.
      const modals = this.do$(".vui-wizard,clr-wizard,:not(clr-wizard)>clr-modal");

      if (!modals || (modals.length === 0)) {
         return undefined;
      }

      const hierarchyContext = modals.map((index, htmlElement): ModalInfo => {
         const jqElement = this.do$(htmlElement);
         const modalSize = this.getModalSize(jqElement);
         return {
            title: this.getModalTitleText(jqElement),
            subtitle: this.getModalSubtitleText(jqElement),
            width:  modalSize.width,
            height: modalSize.height
         };
      }).get();

      if (!hierarchyContext) {
         return undefined;
      }

      const result: ModalInfo[] = <any>hierarchyContext;

      return bottomToTop ? result : result.reverse();
   }

   /**
    * Finds the first vui wizard or clarity wizard/dialog and returns its current width and height
    */
   public retrieveFirstLevelPopupSizes() {
      // NOTE: We need to collect all popups with a single
      // css selector to have them properly ordered.
      const modals = this.do$(".vui-wizard,clr-wizard,:not(clr-wizard)>clr-modal");

      if (!modals || (modals.length === 0)) {
         return undefined;
      }

      const jqElement = this.do$(modals[0]);
      const modalSize = this.getModalSize(jqElement);
      return {
         width:  modalSize.width,
         height: modalSize.height
      };
   }

   private getModalSize(jqElement: any): { width: string, height: string } {
      if (jqElement.hasClass("vui-wizard")) {
         return this.getVuiWizardSize(jqElement);
      }

      if (jqElement.hasClass("clr-wizard")) {
         return this.getClrWizardSize(jqElement);
      }

      return this.getClrModalSize(jqElement);
   }

   private getVuiWizardSize(
         jqVuiWizardElement: any
   ): { width: string, height: string } {
      return {
         width: jqVuiWizardElement.outerWidth() + "px",
         height: jqVuiWizardElement.outerHeight() + "px",
      };
   }

   private getClrWizardSize(
         jqElement: any
   ): { width: string, height: string } {
      const modalContent = jqElement.find(".modal-content-wrapper");
      return {
         width: modalContent.outerWidth() + "px",
         height: modalContent.outerHeight() + "px",
      };
   }

   private getClrModalSize(
         jqElement: any
   ): { width: string, height: string } {
      const modalContent = jqElement.find(".modal-content");
      return {
         width: modalContent.outerWidth() + "px",
         height: modalContent.outerHeight() + "px",
      };
   }

   private getModalTitleText(
         jqElement: any
   ): string | undefined {
      if (jqElement.hasClass("vui-wizard")) {
         return this.getVuiWizardTitleText(jqElement);
      }

      if (jqElement.hasClass("clr-wizard")) {
         return this.getClrWizardTitleText(jqElement);
      }

      return this.getClrModalTitleText(jqElement);
   }

   private getVuiWizardTitleText(
         jqVuiWizardElement: any
   ): string | undefined {
      const titleElement = jqVuiWizardElement.find(".titlebar-text");
      if (!titleElement) {
         return undefined;
      }

      return titleElement.text();
   }

   private getClrWizardTitleText(
         jqElement: any
   ): string | undefined {
      const titleElement = jqElement.find("clr-wizard-title");
      if (!titleElement) {
         return undefined;
      }

      return titleElement.text();
   }

   private getClrModalTitleText(
         jqElement: any
   ): string | undefined {
      const titleElement = jqElement.find(".primaryTitle");
      if (!titleElement) {
         return undefined;
      }

      return titleElement.text();
   }


   private getModalSubtitleText(
         jqElement: any
   ): string | undefined {
      if (jqElement.hasClass("vui-wizard")) {
         return this.getVuiWizardCurrentStepText(jqElement);
      }

      if (jqElement.hasClass("clr-wizard")) {
         return this.getClrWizardCurrentStepText(jqElement);
      }

      return this.getClrModalSecondaryTitleText(jqElement);
   }

   private getVuiWizardCurrentStepText(
         jqVuiWizardElement: any
   ): string | undefined {
      const stepElement = jqVuiWizardElement.find(".wizard-steps-current");
      if (!stepElement) {
         return undefined;
      }

      return stepElement.text();
   }

   private getClrWizardCurrentStepText(
         jqElement: any
   ): string | undefined {
      const stepElement = jqElement.find(".modal-title-text");
      if (!stepElement) {
         return undefined;
      }

      return stepElement.text();
   }

   private getClrModalSecondaryTitleText(
         jqElement: any
   ): string | undefined {
      const titleElement = jqElement.find(".secondaryTitle");
      if (!titleElement) {
         return undefined;
      }

      return titleElement.text();
   }

   /**
    * A helper method we can mock to simulate different results from jQuery calls.
    */
   private do$(selector: any) {
      return $(selector);
   }

} // class ModalHelperService

angular.module("com.vmware.platform.ui")
      .service("modalHelperService", ModalHelperService);
} // module
